import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import TopBar from "../components/TopBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Neon green accent for the Elite sub-brand (distinct from the pink main brand).
const GREEN = "#39ff14";

/**
 * SoccerTroopElitePage — /elite
 * 12-section premium marketing page + application flow.
 * Extends existing SoccerTroop ecosystem; does not replace any current feature.
 */
export default function SoccerTroopElitePage() {
  const { lang } = useLang();
  const es = lang === "es";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopBar />
      <HeroSection es={es} />
      <ConceptSection es={es} />
      <WhyEliteSection es={es} />
      <EarnPlaceSection es={es} />
      <SummerSelectSection es={es} />
      <PlayerProfileSection es={es} />
      <VideoExposureSection es={es} />
      <InternationalSection es={es} />
      <FuturePathwaySection es={es} />
      <ForParentsSection es={es} />
      <ApplicationSection es={es} />
      <FinalCtaSection es={es} />
      <footer className="border-t border-[#111] py-8 text-center text-xs text-neutral-600">
        SOCCERTROOP · ELITE · Denver, Colorado
      </footer>
    </div>
  );
}

// ============ Reusable primitives ============
function EliteBadge({ children }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.3em] font-medium"
      style={{
        color: GREEN,
        borderColor: `${GREEN}40`,
        backgroundColor: `${GREEN}0d`,
        border: "1px solid",
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GREEN, boxShadow: `0 0 8px ${GREEN}` }} />
      {children}
    </span>
  );
}

function SectionShell({ children, className = "", eyebrow, id }) {
  return (
    <section id={id} className={`relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 ${className}`}>
      {eyebrow && <div className="mb-4"><EliteBadge>{eyebrow}</EliteBadge></div>}
      {children}
    </section>
  );
}

function PathwayArrow() {
  return (
    <div className="flex justify-center py-1.5">
      <div className="text-lg leading-none" style={{ color: GREEN, textShadow: `0 0 12px ${GREEN}` }}>↓</div>
    </div>
  );
}

function PathwayStep({ label, glow = false, tone = "default" }) {
  const isFuture = tone === "future";
  return (
    <div
      className={`w-full rounded-xl px-4 py-3.5 text-center font-bebas tracking-[0.2em] text-lg sm:text-xl border transition-all ${
        isFuture ? "border-dashed border-neutral-700 text-neutral-500" : "border-[#1a1a1a] text-white"
      }`}
      style={
        glow
          ? { borderColor: GREEN, backgroundColor: `${GREEN}0f`, boxShadow: `0 0 24px ${GREEN}20` }
          : { backgroundColor: "#0a0a0a" }
      }
    >
      {label}
    </div>
  );
}

// ============ Hero ============
function HeroSection({ es }) {
  return (
    <section className="relative overflow-hidden">
      {/* Grain/vignette background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 20% 30%, ${GREEN}18 0%, transparent 50%), radial-gradient(circle at 80% 70%, ${GREEN}10 0%, transparent 55%)`,
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-28">
        <EliteBadge>
          {es ? "PROGRAMA SELECTIVO · INVITACIÓN" : "SELECTIVE PROGRAM · BY INVITATION"}
        </EliteBadge>
        <h1
          className="mt-5 font-bebas leading-[0.9] tracking-[0.01em] text-6xl sm:text-8xl lg:text-9xl"
          style={{ textShadow: `0 0 30px ${GREEN}25` }}
        >
          <span className="block">SOCCERTROOP</span>
          <span className="block" style={{ color: GREEN }}>ELITE</span>
        </h1>
        <div className="mt-8 font-bebas text-3xl sm:text-5xl leading-[1.05] tracking-widest">
          {es ? (
            <>
              <div>TU CLUB.</div>
              <div>TU CAMINO.</div>
              <div>TU SIGUIENTE NIVEL.</div>
            </>
          ) : (
            <>
              <div>YOUR CLUB.</div>
              <div>YOUR JOURNEY.</div>
              <div>YOUR NEXT LEVEL.</div>
            </>
          )}
        </div>
        <div className="mt-6 text-sm sm:text-base text-neutral-300 max-w-xl leading-relaxed">
          {es
            ? "Mantén tu club. Entrena diferente. Compite junto a jugadoras seleccionadas. Crea nuevas oportunidades."
            : "Keep your club. Train differently. Compete together. Create opportunities."}
        </div>
        <p className="mt-4 text-sm text-neutral-400 max-w-2xl leading-relaxed">
          {es
            ? "Una experiencia de desarrollo y Summer Select por invitación para jugadoras ambiciosas que quieren más de su desarrollo individual."
            : "An invitation-only development and Summer Select experience for ambitious female soccer players who want more from their individual development."}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="#apply"
            data-testid="elite-hero-apply"
            className="px-7 py-4 rounded-lg font-bebas text-lg tracking-[0.25em] text-black transition-all hover:scale-[1.02]"
            style={{ backgroundColor: GREEN, boxShadow: `0 0 24px ${GREEN}55` }}
          >
            {es ? "APLICAR A ELITE" : "APPLY FOR ELITE"} →
          </a>
          <a
            href="#how-it-works"
            data-testid="elite-hero-how"
            className="px-7 py-4 rounded-lg font-bebas text-lg tracking-[0.25em] text-white border transition-all hover:text-black hover:bg-white"
            style={{ borderColor: "#333" }}
          >
            {es ? "CÓMO FUNCIONA" : "HOW ELITE WORKS"}
          </a>
        </div>
      </div>
    </section>
  );
}

// ============ Section 1: The Concept ============
function ConceptSection({ es }) {
  const steps = [
    es ? "TU CLUB" : "YOUR CLUB",
    es ? "SOCCERTROOP DEVELOPMENT" : "SOCCERTROOP DEVELOPMENT",
    "SOCCERTROOP ELITE",
    es ? "SUMMER SELECT" : "SUMMER SELECT",
    es ? "SHOWCASES Y TORNEOS" : "SHOWCASES & TOURNAMENTS",
    es ? "NUEVAS OPORTUNIDADES" : "NEW OPPORTUNITIES",
  ];
  return (
    <SectionShell id="how-it-works" eyebrow={es ? "EL CONCEPTO" : "THE CONCEPT"}>
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-3xl">
        {es ? (
          <>
            MANTÉN TU CLUB.
            <br />
            <span style={{ color: GREEN }}>SUMA SOCCERTROOP.</span>
          </>
        ) : (
          <>
            KEEP YOUR CLUB.
            <br />
            <span style={{ color: GREEN }}>ADD SOCCERTROOP.</span>
          </>
        )}
      </h2>
      <p className="mt-6 text-base sm:text-lg text-neutral-300 max-w-2xl leading-relaxed">
        {es
          ? "SoccerTroop Elite está diseñado para complementar —no reemplazar— al club principal de tu jugadora."
          : "SoccerTroop Elite is designed to complement — not replace — your player's primary soccer club."}
      </p>
      <p className="mt-3 text-sm text-neutral-400 max-w-2xl leading-relaxed">
        {es
          ? "Las jugadoras siguen entrenando y compitiendo con sus equipos regulares mientras SoccerTroop ofrece un ambiente adicional enfocado en desarrollo individual, definición, toma de decisiones, confianza y oportunidades selectas de competencia veraniega."
          : "Players continue training and competing with their regular teams while SoccerTroop provides an additional environment focused on individual development, finishing, decision-making, confidence and selected summer competition opportunities."}
      </p>
      <div className="mt-10 max-w-md mx-auto">
        {steps.map((s, i) => (
          <div key={i}>
            <PathwayStep label={s} glow={i === 2 || i === 5} />
            {i < steps.length - 1 && <PathwayArrow />}
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

// ============ Section 2: Why Elite Exists ============
function WhyEliteSection({ es }) {
  const cards = [
    {
      n: "01",
      title: es ? "DEFINICIÓN" : "FINISHING",
      body: es
        ? "Diferentes técnicas de definición, remate bajo presión, desarrollo del pie no dominante y decisiones al momento de anotar."
        : "Different finishing techniques, shooting under pressure, weak-foot development and scoring decisions.",
    },
    {
      n: "02",
      title: es ? "TOMA DE DECISIONES" : "DECISION MAKING",
      body: es
        ? "Las jugadoras aprenden a reconocer las situaciones antes de recibir el balón y tomar decisiones más rápidas."
        : "Players learn to recognize situations before receiving the ball and make faster decisions.",
    },
    {
      n: "03",
      title: es ? "MÉTODO DEL RELOJ" : "THE CLOCK METHOD",
      body: es
        ? "Usamos la metodología propietaria de SoccerTroop para enseñar conciencia espacial, postura corporal, escaneo y toma de decisiones."
        : "We use the proprietary SoccerTroop training methodology to teach spatial awareness, body positioning, scanning and decision-making.",
    },
    {
      n: "04",
      title: es ? "MENTALIDAD" : "MENTALITY",
      body: es
        ? "Confianza. Coraje. Disciplina. Resiliencia. Toma de decisiones bajo presión."
        : "Confidence. Courage. Discipline. Resilience. Decision making under pressure.",
    },
  ];
  return (
    <SectionShell eyebrow={es ? "POR QUÉ EXISTE ELITE" : "WHY SOCCERTROOP ELITE EXISTS"}>
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-3xl">
        {es ? (
          <>
            NO CONSTRUIMOS OTRO EQUIPO.
            <br />
            <span style={{ color: GREEN }}>CONSTRUIMOS MEJORES JUGADORAS.</span>
          </>
        ) : (
          <>
            WE DON&apos;T BUILD ANOTHER TEAM.
            <br />
            <span style={{ color: GREEN }}>WE BUILD BETTER PLAYERS.</span>
          </>
        )}
      </h2>
      <p className="mt-6 text-sm sm:text-base text-neutral-300 max-w-2xl leading-relaxed">
        {es
          ? "Muchas jugadoras ya reciben entrenamiento táctico y de equipo desde sus clubes. SoccerTroop se enfoca en el individuo."
          : "Many players already receive tactical and team training from their clubs. SoccerTroop focuses on the individual."}
      </p>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cards.map((c) => (
          <div
            key={c.n}
            className="rounded-2xl border p-6 bg-gradient-to-b from-[#0a0a0a] to-[#050505] transition-all hover:border-opacity-100"
            style={{ borderColor: "#1a1a1a" }}
          >
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-bebas text-3xl tracking-widest" style={{ color: GREEN }}>{c.n}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">CORE</div>
            </div>
            <h3 className="font-bebas text-2xl sm:text-3xl tracking-widest text-white mb-2">
              {c.title}
            </h3>
            <p className="text-sm text-neutral-400 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-12 py-8 border-y" style={{ borderColor: `${GREEN}30` }}>
        <div
          className="font-bebas text-4xl sm:text-6xl text-center leading-tight tracking-widest"
          style={{ textShadow: `0 0 24px ${GREEN}30` }}
        >
          {es ? "ENTRENA LA MENTE." : "TRAIN THE BRAIN."}
          <br />
          <span style={{ color: GREEN }}>{es ? "DOMINA LA DEFINICIÓN." : "MASTER THE FINISH."}</span>
        </div>
      </div>
    </SectionShell>
  );
}

// ============ Section 3: Earn Your Place ============
function EarnPlaceSection({ es }) {
  return (
    <SectionShell eyebrow={es ? "GÁNATE TU LUGAR" : "EARN YOUR PLACE"}>
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight">
        {es ? "ELITE NO ES PARA TODOS." : "ELITE IS NOT FOR EVERYONE."}
        <br />
        <span style={{ color: GREEN }}>{es ? "GÁNATE TU LUGAR." : "EARN YOUR PLACE."}</span>
      </h2>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border p-6 bg-[#0a0a0a]" style={{ borderColor: `${GREEN}30` }}>
          <div className="font-bebas text-3xl tracking-widest mb-2" style={{ color: GREEN }}>{es ? "POR INVITACIÓN" : "BY INVITATION"}</div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            {es
              ? "Nuestro cuerpo técnico identifica jugadoras que muestran la actitud, el compromiso y el nivel necesarios."
              : "Our coaching staff identifies players who show the attitude, commitment and level required."}
          </p>
        </div>
        <div className="rounded-2xl border p-6 bg-[#0a0a0a]" style={{ borderColor: `${GREEN}30` }}>
          <div className="font-bebas text-3xl tracking-widest mb-2" style={{ color: GREEN }}>{es ? "APLICACIÓN + EVALUACIÓN" : "APPLICATION + EVALUATION"}</div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            {es
              ? "Las jugadoras pueden aplicar y ser convocadas a una sesión de evaluación con nuestro cuerpo técnico."
              : "Players can apply and be invited to an evaluation session with our coaching staff."}
          </p>
        </div>
      </div>
      <div className="mt-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
          {es ? "PERFIL IDEAL" : "IDEAL PLAYER PROFILE"}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-3xl">
          {(es
            ? [
                "Jugadoras ambiciosas",
                "Jugadoras que compiten actualmente para un club",
                "Comprometidas con mejorar individualmente",
                "Dispuestas a entrenar fuera de la zona de confort",
                "Interesadas en competencia de mayor nivel",
                "Interesadas en oportunidades futuras",
              ]
            : [
                "Ambitious female players",
                "Players currently competing for a soccer club",
                "Players committed to improving individually",
                "Players willing to train outside their comfort zone",
                "Players interested in higher-level competition",
                "Players interested in future opportunities",
              ]
          ).map((it, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-neutral-300">
              <span style={{ color: GREEN }}>▸</span> {it}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8">
        <a
          href="#apply"
          data-testid="elite-earn-apply"
          className="inline-block px-6 py-3 rounded-lg font-bebas text-base tracking-widest text-black transition-all hover:scale-[1.02]"
          style={{ backgroundColor: GREEN, boxShadow: `0 0 20px ${GREEN}40` }}
        >
          {es ? "APLICAR A ELITE" : "APPLY FOR ELITE"} →
        </a>
      </div>
    </SectionShell>
  );
}

// ============ Section 4: Summer Select ============
function SummerSelectSection({ es }) {
  const steps = [
    es ? "SELECCIÓN DE JUGADORAS" : "PLAYER SELECTION",
    es ? "ENTRENAMIENTO ELITE" : "ELITE TRAINING",
    es ? "PREPARACIÓN DE EQUIPO" : "TEAM PREPARATION",
    es ? "TORNEOS DE VERANO" : "SUMMER TOURNAMENTS",
    es ? "OPORTUNIDADES DE SHOWCASE" : "SHOWCASE OPPORTUNITIES",
    es ? "REVISIÓN DE JUGADORAS" : "PLAYER REVIEW",
  ];
  return (
    <SectionShell eyebrow="SUMMER SELECT">
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-3xl">
        {es ? (
          <>
            TU CLUB DURANTE LA TEMPORADA.
            <br />
            <span style={{ color: GREEN }}>SOCCERTROOP ELITE EN VERANO.</span>
          </>
        ) : (
          <>
            YOUR CLUB DURING THE SEASON.
            <br />
            <span style={{ color: GREEN }}>SOCCERTROOP ELITE IN THE SUMMER.</span>
          </>
        )}
      </h2>
      <p className="mt-6 text-sm sm:text-base text-neutral-300 max-w-2xl leading-relaxed">
        {es
          ? "Jugadoras seleccionadas de SoccerTroop Elite pueden ser invitadas a entrenar juntas y representar a SoccerTroop Elite en torneos, showcases y experiencias competitivas cuidadosamente seleccionadas durante el verano."
          : "Selected SoccerTroop Elite players may be invited to train together and represent SoccerTroop Elite in carefully selected summer tournaments, showcases and competitive experiences."}
      </p>
      <p className="mt-3 text-sm text-neutral-500 max-w-2xl italic">
        {es
          ? "La intención NO es mover permanentemente a las jugadoras de sus clubes actuales."
          : "The intention is NOT to permanently move players away from their current clubs."}
      </p>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl">
        {(es
          ? ["Disponibilidad", "Elegibilidad", "Reglamentos de torneos", "Registro y federación"]
          : ["Availability", "Eligibility", "Tournament regulations", "Registration & federation"]
        ).map((r, i) => (
          <div key={i} className="rounded-md px-2.5 py-1.5 text-[10px] uppercase tracking-widest bg-[#0a0a0a] border border-[#1a1a1a] text-neutral-400 text-center">
            {r}
          </div>
        ))}
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-6 gap-2 items-stretch">
        {steps.map((s, i) => (
          <div
            key={i}
            className="rounded-xl border p-3.5 text-center font-bebas tracking-widest text-sm bg-[#0a0a0a]"
            style={{ borderColor: i === 3 ? GREEN : "#1a1a1a", boxShadow: i === 3 ? `0 0 20px ${GREEN}25` : "none" }}
          >
            <div className="text-[10px] mb-1" style={{ color: GREEN }}>{String(i + 1).padStart(2, "0")}</div>
            <div className="text-white leading-tight">{s}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap gap-3 justify-start">
        <span className="px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border" style={{ borderColor: `${GREEN}40`, color: GREEN }}>
          {es ? "ROSTER LIMITADO" : "LIMITED ROSTER"}
        </span>
        <span className="px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border" style={{ borderColor: `${GREEN}40`, color: GREEN }}>
          {es ? "POR INVITACIÓN / SELECCIÓN" : "BY INVITATION / SELECTION"}
        </span>
      </div>
    </SectionShell>
  );
}

// ============ Section 5: Player Development Profile ============
function PlayerProfileSection({ es }) {
  const stats = [
    { k: es ? "DEFINICIÓN" : "FINISHING", v: 87 },
    { k: es ? "MOVIMIENTO" : "MOVEMENT", v: 84 },
    { k: es ? "TOMA DE DECISIONES" : "DECISION MAKING", v: 82 },
    { k: es ? "PIE NO DOMINANTE" : "WEAK FOOT", v: 78 },
    { k: es ? "CONFIANZA" : "CONFIDENCE", v: 89 },
  ];
  const bullets = es
    ? ["Historial de entrenamientos", "Evaluaciones del coach", "Notas de progreso", "Videos Antes/Después", "Desarrollo del remate", "Habilidades trabajadas", "Metas e hitos", "Participación en torneos", "Videos de highlights"]
    : ["Training history", "Coach evaluations", "Progress notes", "Before/After videos", "Shooting development", "Skills being worked on", "Goals and milestones", "Tournament participation", "Highlight videos"];
  return (
    <SectionShell eyebrow={es ? "PERFIL DE DESARROLLO" : "PLAYER DEVELOPMENT PROFILE"}>
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-3xl">
        {es ? (
          <>
            CADA JUGADORA TIENE UNA HISTORIA.
            <br />
            <span style={{ color: GREEN }}>DOCUMENTAMOS EL DESARROLLO.</span>
          </>
        ) : (
          <>
            EVERY PLAYER HAS A STORY.
            <br />
            <span style={{ color: GREEN }}>WE DOCUMENT THE DEVELOPMENT.</span>
          </>
        )}
      </h2>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Example player card */}
        <div
          className="rounded-2xl border p-6 sm:p-7 bg-gradient-to-br from-[#0a0a0a] to-[#050505]"
          style={{ borderColor: `${GREEN}30` }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center font-bebas text-3xl"
              style={{ backgroundColor: `${GREEN}12`, border: `1px solid ${GREEN}40`, color: GREEN }}
            >
              SM
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{es ? "PERFIL DE EJEMPLO" : "SAMPLE PROFILE"}</div>
              <div className="font-bebas text-3xl tracking-widest">SOFIA MARTINEZ</div>
              <div className="text-xs text-neutral-400 mt-0.5">
                {es ? "Delantera · 16 años · Pie: derecho" : "Forward · Age 16 · Preferred foot: right"}
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {stats.map((s) => (
              <div key={s.k}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="uppercase tracking-[0.15em] text-neutral-300">{s.k}</span>
                  <span className="font-bebas text-lg tracking-widest" style={{ color: GREEN }}>{s.v}</span>
                </div>
                <div className="h-1.5 bg-[#111] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${s.v}%`, background: `linear-gradient(90deg, ${GREEN}, ${GREEN}80)`, boxShadow: `0 0 8px ${GREEN}80` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Bullet list */}
        <div>
          <p className="text-sm text-neutral-400 leading-relaxed mb-5">
            {es
              ? "Cada jugadora Elite tiene un perfil digital de desarrollo conectado al sistema existente de jugadores/padres de SoccerTroop. Los padres acceden desde su cuenta actual. El admin actualiza desde el dashboard existente."
              : "Every Elite player has a digital development profile connected to the existing SoccerTroop player/parent system. Parents access it through their existing account. Admin updates it via the existing admin dashboard."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {bullets.map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-neutral-300 py-1">
                <span style={{ color: GREEN }}>◆</span>
                {b}
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

// ============ Section 6: Video & Exposure ============
function VideoExposureSection({ es }) {
  const items = es
    ? ["Perfil de jugadora", "Video de highlights", "Metraje de entrenamiento", "CV deportivo", "Información de posición", "Historia de desarrollo"]
    : ["Player profile", "Highlight video", "Training footage", "Soccer résumé", "Position information", "Development history"];
  return (
    <SectionShell eyebrow={es ? "VIDEO Y EXPOSICIÓN" : "PLAYER VIDEO & EXPOSURE"}>
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-3xl">
        {es ? (
          <>
            CUANDO ESTÉS LISTA,
            <br />
            <span style={{ color: GREEN }}>TE AYUDAMOS A MOSTRARLO.</span>
          </>
        ) : (
          <>
            WHEN YOU&apos;RE READY,
            <br />
            <span style={{ color: GREEN }}>WE HELP YOU SHOW IT.</span>
          </>
        )}
      </h2>
      <p className="mt-6 text-sm sm:text-base text-neutral-300 max-w-2xl leading-relaxed">
        {es
          ? "Jugadoras seleccionadas pueden recibir ayuda para crear material profesional de desarrollo, tal como:"
          : "Selected players may receive assistance creating professional player-development material such as:"}
      </p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl border border-[#1a1a1a] px-4 py-3.5 bg-[#0a0a0a] text-sm text-neutral-200">
            <span style={{ color: GREEN }}>▸</span> {it}
          </div>
        ))}
      </div>
      <p className="mt-8 text-xs text-neutral-500 italic max-w-2xl leading-relaxed">
        {es
          ? "SoccerTroop se refiere a oportunidades, exposición y conexiones — cuando sea apropiado y cuando la jugadora esté lista — sujeto a elegibilidad y regulaciones aplicables. No prometemos becas, contratos profesionales, tryouts, reclutamiento ni representación."
          : "SoccerTroop refers to opportunities, exposure and connections — when appropriate and when the player is ready — subject to eligibility and applicable regulations. We do not promise scholarships, professional contracts, trials, recruitment or representation."}
      </p>
    </SectionShell>
  );
}

// ============ Section 7: International Pathway ============
function InternationalSection({ es }) {
  return (
    <section className="relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 15% 40%, ${GREEN}18 0%, transparent 45%), radial-gradient(circle at 85% 60%, ${GREEN}14 0%, transparent 50%)`,
        }}
      />
      <SectionShell eyebrow={es ? "CAMINO INTERNACIONAL" : "INTERNATIONAL PATHWAY"} className="!py-20 sm:!py-32">
        <h2 className="font-bebas text-6xl sm:text-8xl leading-[0.9] tracking-tight">
          <div>COLORADO <span style={{ color: GREEN }}>→</span></div>
          <div>MEXICO <span style={{ color: GREEN }}>→</span></div>
          <div style={{ color: GREEN, textShadow: `0 0 32px ${GREEN}50` }}>BEYOND.</div>
        </h2>
        <div className="mt-6 font-bebas text-2xl sm:text-3xl tracking-widest text-neutral-300">
          {es ? "EL DESARROLLO CREA OPORTUNIDADES." : "DEVELOPMENT CAN CREATE OPPORTUNITIES."}
        </div>
        <p className="mt-6 text-sm sm:text-base text-neutral-300 max-w-2xl leading-relaxed">
          {es
            ? "SoccerTroop está construyendo relaciones y conexiones diseñadas para ayudar a jugadoras seleccionadas a acceder a experiencias adicionales y oportunidades de exposición en Estados Unidos, México e internacionalmente."
            : "SoccerTroop is building relationships and connections designed to help selected players access additional soccer experiences and exposure opportunities in the United States, Mexico and internationally."}
        </p>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-3xl">
          {(es
            ? ["Experiencias de entrenamiento", "Eventos competitivos", "Showcases", "Presentaciones de jugadora", "Experiencias internacionales", "Oportunidades de desarrollo"]
            : ["Training experiences", "Competitive events", "Showcases", "Player introductions", "International experiences", "Development opportunities"]
          ).map((x, i) => (
            <div key={i} className="rounded-xl border border-[#1a1a1a] px-3 py-2.5 bg-[#0a0a0a]/60 text-xs text-neutral-200">
              <span style={{ color: GREEN }}>◆</span> {x}
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-neutral-500 italic max-w-2xl leading-relaxed">
          {es
            ? "SoccerTroop no promete contratos profesionales ni pruebas profesionales, y no actúa actualmente como agencia de fútbol."
            : "SoccerTroop does not promise professional contracts or professional trials, and does not currently act as a football agency."}
        </p>
      </SectionShell>
    </section>
  );
}

// ============ Section 8: Future Pathway ============
function FuturePathwaySection({ es }) {
  const steps = [
    es ? "ENTRENAR" : "TRAIN",
    es ? "DESARROLLAR" : "DEVELOP",
    es ? "COMPETIR" : "COMPETE",
    es ? "SER VISTA" : "GET SEEN",
    es ? "CREAR OPORTUNIDADES" : "CREATE OPPORTUNITIES",
    es ? "GUÍA PROFESIONAL" : "PROFESSIONAL GUIDANCE",
  ];
  return (
    <SectionShell eyebrow={es ? "CAMINO FUTURO" : "THE FUTURE PATHWAY"}>
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-3xl">
        {es ? (
          <>
            DESARROLLO HOY.
            <br />
            <span style={{ color: GREEN }}>OPORTUNIDADES MAÑANA.</span>
          </>
        ) : (
          <>
            DEVELOPMENT TODAY.
            <br />
            <span style={{ color: GREEN }}>OPPORTUNITIES TOMORROW.</span>
          </>
        )}
      </h2>
      <div className="mt-10 max-w-md mx-auto">
        {steps.map((s, i) => (
          <div key={i}>
            <PathwayStep label={s} glow={i === steps.length - 1} tone={i === steps.length - 1 ? "future" : "default"} />
            {i < steps.length - 1 && <PathwayArrow />}
          </div>
        ))}
      </div>
      <div className="mt-10 max-w-3xl mx-auto text-center">
        <EliteBadge>{es ? "PRÓXIMAMENTE" : "COMING IN THE FUTURE"}</EliteBadge>
        <h3 className="mt-3 font-bebas text-3xl sm:text-4xl tracking-widest text-white">
          {es ? "GUÍA PROFESIONAL DE FÚTBOL" : "PROFESSIONAL FOOTBALL GUIDANCE"}
        </h3>
        <p className="mt-4 text-sm text-neutral-400 leading-relaxed">
          {es
            ? "SoccerTroop está construyendo un camino de largo plazo para jugadoras que va más allá del entrenamiento. En el futuro, jugadoras elegibles podrán tener la oportunidad de acceder a guía profesional de fútbol y potencialmente trabajar con un Agente FIFA Licenciado, sujeto a las regulaciones aplicables de FIFA, requisitos de elegibilidad y acuerdos separados de representación."
            : "SoccerTroop is building a long-term player pathway that goes beyond training. In the future, eligible players may have the opportunity to access professional football guidance and potentially work with a FIFA Licensed Football Agent, subject to applicable FIFA regulations, eligibility requirements and separate representation agreements."}
        </p>
        <div className="mt-6 rounded-xl border p-4 sm:p-5 text-left text-xs text-neutral-400 leading-relaxed max-w-2xl mx-auto" style={{ borderColor: "#1a1a1a", backgroundColor: "#0a0a0a" }}>
          <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: GREEN }}>
            {es ? "NOTA LEGAL Y ÉTICA" : "LEGAL AND ETHICAL NOTE"}
          </div>
          {es
            ? "La participación en SoccerTroop o SoccerTroop Elite NO requiere que ninguna jugadora contrate a un agente de fútbol en particular. La representación por agente de fútbol es completamente separada del entrenamiento SoccerTroop y de la participación en SoccerTroop Elite. Cualquier servicio de agente de fútbol se ofrecerá únicamente por personas debidamente licenciadas y mediante acuerdos separados conforme a las regulaciones aplicables."
            : "Participation in SoccerTroop or SoccerTroop Elite does NOT require any player to hire a particular football agent. Football-agent representation is completely separate from SoccerTroop training and SoccerTroop Elite participation. Any football-agent services will only be offered by appropriately licensed individuals and through separate agreements in accordance with applicable regulations."}
        </div>
      </div>
    </SectionShell>
  );
}

// ============ Section 9: For Parents ============
function ForParentsSection({ es }) {
  const items = es
    ? ["Desarrollo individual", "Entrenamientos Elite en grupos pequeños", "Evaluaciones de jugadora", "Seguimiento de progreso", "Videos de entrenamiento", "Historia de desarrollo", "Oportunidades de competencia en verano", "Comunicación clara con padres", "Oportunidades de exposición internacional cuando sea apropiado"]
    : ["Individual development", "Small-group Elite training", "Player evaluations", "Progress tracking", "Training videos", "Development history", "Summer competition opportunities", "Clear parent communication", "International exposure opportunities when appropriate"];
  return (
    <SectionShell eyebrow={es ? "PARA PADRES" : "FOR PARENTS"}>
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-3xl">
        {es ? (
          <>
            EL DESARROLLO DE TU JUGADORA.
            <br />
            <span style={{ color: GREEN }}>VISIBLE.</span>
          </>
        ) : (
          <>
            YOUR PLAYER&apos;S DEVELOPMENT.
            <br />
            <span style={{ color: GREEN }}>VISIBLE.</span>
          </>
        )}
      </h2>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-2.5">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl border border-[#1a1a1a] px-4 py-3 bg-[#0a0a0a] text-sm text-neutral-200 flex items-center gap-2">
            <span style={{ color: GREEN }}>◆</span> {it}
          </div>
        ))}
      </div>
      <div className="mt-10 font-bebas text-3xl sm:text-5xl text-center tracking-widest leading-tight">
        {es ? "SIN PROMESAS." : "NO PROMISES."}
        <br />
        {es ? "SIN ATAJOS." : "NO SHORTCUTS."}
        <br />
        <span style={{ color: GREEN }}>{es ? "DESARROLLO PRIMERO." : "DEVELOPMENT FIRST."}</span>
      </div>
    </SectionShell>
  );
}

// ============ Section 10: Application Form ============
function ApplicationSection({ es }) {
  const emptyForm = {
    player_name: "",
    date_of_birth: "",
    age: "",
    primary_position: "",
    secondary_position: "",
    preferred_foot: "",
    current_club: "",
    current_team: "",
    competition_level: "",
    years_playing: "",
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    highlight_video_url: "",
    why_elite: "",
    biggest_goal: "",
    improve_most: "",
    consent: false,
  };
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const successRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) {
      setErr(es ? "Debes confirmar el aviso de aceptación." : "Please confirm the acceptance notice.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age, 10) : null,
        years_playing: form.years_playing ? parseInt(form.years_playing, 10) : null,
        language: es ? "es" : "en",
      };
      delete payload.consent;
      await axios.post(`${API}/elite/applications`, payload);
      setDone(true);
      setTimeout(() => successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : es ? "Error al enviar" : "Submission error");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0a] border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none transition-colors";
  const style = { borderColor: "#1a1a1a" };
  const focusStyle = { borderColor: GREEN };

  if (done) {
    return (
      <SectionShell id="apply" eyebrow={es ? "APLICACIÓN RECIBIDA" : "APPLICATION RECEIVED"}>
        <div
          ref={successRef}
          data-testid="elite-application-success"
          className="rounded-2xl border p-8 sm:p-10 text-center"
          style={{ borderColor: `${GREEN}40`, backgroundColor: `${GREEN}0a` }}
        >
          <div className="text-6xl mb-4">✓</div>
          <h3 className="font-bebas text-4xl sm:text-5xl tracking-widest" style={{ color: GREEN }}>
            {es ? "APLICACIÓN RECIBIDA" : "APPLICATION RECEIVED"}
          </h3>
          <p className="mt-4 text-sm sm:text-base text-neutral-300 max-w-xl mx-auto leading-relaxed">
            {es
              ? "Gracias por aplicar a SoccerTroop Elite. Nuestro cuerpo técnico revisará tu aplicación y contactará al padre/tutor sobre el siguiente paso."
              : "Thank you for applying to SoccerTroop Elite. Our coaching staff will review your application and contact your parent/guardian regarding the next step."}
          </p>
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell id="apply" eyebrow={es ? "APLICACIÓN ELITE" : "ELITE APPLICATION"}>
      <h2 className="font-bebas text-5xl sm:text-7xl leading-[0.95] tracking-tight">
        {es ? (
          <>
            APLICA A
            <br />
            <span style={{ color: GREEN }}>SOCCERTROOP ELITE</span>
          </>
        ) : (
          <>
            APPLY FOR
            <br />
            <span style={{ color: GREEN }}>SOCCERTROOP ELITE</span>
          </>
        )}
      </h2>
      <p className="mt-4 text-sm text-neutral-400 max-w-2xl">
        {es
          ? "Aplicar no garantiza la aceptación. Sé honesta y completa. Nuestro cuerpo técnico revisa cada aplicación."
          : "Applying does not guarantee acceptance. Be honest and complete. Our coaching staff reviews each application."}
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="elite-application-form">
        {/* Player Info */}
        <FormBlock title={es ? "INFORMACIÓN DE LA JUGADORA" : "PLAYER INFORMATION"}>
          <F label={es ? "Nombre completo *" : "Player full name *"}>
            <input required data-testid="elite-player-name" className={inputCls} style={style} value={form.player_name} onChange={(e) => set("player_name", e.target.value)} />
          </F>
          <F label={es ? "Fecha de nacimiento *" : "Date of birth *"}>
            <input required type="date" data-testid="elite-dob" className={inputCls} style={style} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
          </F>
          <F label={es ? "Edad" : "Age"}>
            <input type="number" min={5} max={30} data-testid="elite-age" className={inputCls} style={style} value={form.age} onChange={(e) => set("age", e.target.value)} />
          </F>
          <F label={es ? "Posición principal *" : "Primary position *"}>
            <input required data-testid="elite-position-primary" className={inputCls} style={style} value={form.primary_position} onChange={(e) => set("primary_position", e.target.value)} placeholder="Forward / Midfielder / Defender / Goalkeeper" />
          </F>
          <F label={es ? "Posición secundaria" : "Secondary position"}>
            <input data-testid="elite-position-secondary" className={inputCls} style={style} value={form.secondary_position} onChange={(e) => set("secondary_position", e.target.value)} />
          </F>
          <F label={es ? "Pie dominante *" : "Preferred foot *"}>
            <select required data-testid="elite-foot" className={inputCls} style={style} value={form.preferred_foot} onChange={(e) => set("preferred_foot", e.target.value)}>
              <option value="">—</option>
              <option value="right">{es ? "Derecho" : "Right"}</option>
              <option value="left">{es ? "Izquierdo" : "Left"}</option>
              <option value="both">{es ? "Ambos" : "Both"}</option>
            </select>
          </F>
        </FormBlock>

        {/* Current soccer */}
        <FormBlock title={es ? "INFORMACIÓN ACTUAL DE FÚTBOL" : "CURRENT SOCCER INFORMATION"}>
          <F label={es ? "Club actual" : "Current club"}>
            <input data-testid="elite-current-club" className={inputCls} style={style} value={form.current_club} onChange={(e) => set("current_club", e.target.value)} />
          </F>
          <F label={es ? "Equipo actual" : "Current team"}>
            <input data-testid="elite-current-team" className={inputCls} style={style} value={form.current_team} onChange={(e) => set("current_team", e.target.value)} />
          </F>
          <F label={es ? "Nivel de competencia" : "Competition level"}>
            <input data-testid="elite-competition" className={inputCls} style={style} value={form.competition_level} onChange={(e) => set("competition_level", e.target.value)} placeholder="ECNL / NPL / State / Recreational..." />
          </F>
          <F label={es ? "Años jugando competitivo" : "Years playing competitive"}>
            <input type="number" min={0} max={30} data-testid="elite-years" className={inputCls} style={style} value={form.years_playing} onChange={(e) => set("years_playing", e.target.value)} />
          </F>
        </FormBlock>

        {/* Parent */}
        <FormBlock title={es ? "PADRE / TUTOR" : "PARENT / GUARDIAN"}>
          <F label={es ? "Nombre del padre/tutor *" : "Parent/guardian name *"}>
            <input required data-testid="elite-parent-name" className={inputCls} style={style} value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} />
          </F>
          <F label="Email *">
            <input required type="email" data-testid="elite-parent-email" className={inputCls} style={style} value={form.parent_email} onChange={(e) => set("parent_email", e.target.value)} />
          </F>
          <F label={es ? "Teléfono *" : "Phone *"}>
            <input required data-testid="elite-parent-phone" className={inputCls} style={style} value={form.parent_phone} onChange={(e) => set("parent_phone", e.target.value)} />
          </F>
        </FormBlock>

        {/* Player profile */}
        <FormBlock title={es ? "PERFIL DE LA JUGADORA" : "PLAYER PROFILE"}>
          <F label={es ? "URL de video de highlights (opcional)" : "Highlight video URL (optional)"} span={2}>
            <input data-testid="elite-highlight-url" className={inputCls} style={style} value={form.highlight_video_url} onChange={(e) => set("highlight_video_url", e.target.value)} placeholder="https://youtube.com/..." />
          </F>
          <F label={es ? "¿Por qué quieres unirte a SoccerTroop Elite? *" : "Why do you want to join SoccerTroop Elite? *"} span={2}>
            <textarea required rows={3} data-testid="elite-why" className={`${inputCls} resize-none`} style={style} value={form.why_elite} onChange={(e) => set("why_elite", e.target.value)} />
          </F>
          <F label={es ? "¿Cuál es tu meta más grande como jugadora? *" : "What is your biggest goal as a soccer player? *"} span={2}>
            <textarea required rows={2} data-testid="elite-goal" className={`${inputCls} resize-none`} style={style} value={form.biggest_goal} onChange={(e) => set("biggest_goal", e.target.value)} />
          </F>
          <F label={es ? "¿Qué parte de tu juego más quieres mejorar? *" : "What part of your game do you most want to improve? *"} span={2}>
            <textarea required rows={2} data-testid="elite-improve" className={`${inputCls} resize-none`} style={style} value={form.improve_most} onChange={(e) => set("improve_most", e.target.value)} />
          </F>
        </FormBlock>

        <label className="flex items-start gap-3 text-sm text-neutral-300 cursor-pointer">
          <input
            type="checkbox"
            required
            data-testid="elite-consent"
            checked={form.consent}
            onChange={(e) => set("consent", e.target.checked)}
            className="mt-1 w-4 h-4"
            style={{ accentColor: GREEN }}
          />
          <span>
            {es
              ? "Entiendo que aplicar no garantiza la aceptación en SoccerTroop Elite."
              : "I understand that applying does not guarantee acceptance into SoccerTroop Elite."}
          </span>
        </label>

        {err && (
          <div className="rounded-lg border border-[#e05555]/40 bg-[#e05555]/10 text-[#e05555] p-3 text-sm">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="elite-submit-btn"
          className="w-full sm:w-auto px-8 py-4 rounded-lg font-bebas text-xl tracking-[0.25em] text-black transition-all hover:scale-[1.02] disabled:opacity-60"
          style={{ backgroundColor: GREEN, boxShadow: `0 0 24px ${GREEN}55` }}
        >
          {busy ? "..." : es ? "ENVIAR APLICACIÓN" : "SUBMIT ELITE APPLICATION"} →
        </button>
      </form>
    </SectionShell>
  );
}

function FormBlock({ title, children }) {
  return (
    <div className="rounded-2xl border border-[#1a1a1a] bg-[#080808] p-5 sm:p-6">
      <div className="text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: GREEN }}>{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function F({ label, children, span = 1 }) {
  return (
    <label className={span === 2 ? "sm:col-span-2" : ""}>
      <span className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

// ============ Section 12: Final CTA ============
function FinalCtaSection({ es }) {
  return (
    <section className="relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${GREEN}22 0%, transparent 60%)`,
        }}
      />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
        <h2
          className="font-bebas text-6xl sm:text-8xl leading-[0.9] tracking-tight"
          style={{ textShadow: `0 0 40px ${GREEN}30` }}
        >
          {es ? (
            <>
              ¿ESTÁS LISTA
              <br />
              PARA <span style={{ color: GREEN }}>GANARTE TU LUGAR?</span>
            </>
          ) : (
            <>
              ARE YOU READY
              <br />
              TO <span style={{ color: GREEN }}>EARN YOUR PLACE?</span>
            </>
          )}
        </h2>
        <div className="mt-6 font-bebas text-2xl sm:text-3xl tracking-widest text-neutral-300">
          {es ? (
            <>
              TU CLUB. TU CAMINO.
              <br />
              <span style={{ color: GREEN }}>TU SIGUIENTE NIVEL.</span>
            </>
          ) : (
            <>
              YOUR CLUB. YOUR JOURNEY.
              <br />
              <span style={{ color: GREEN }}>YOUR NEXT LEVEL.</span>
            </>
          )}
        </div>
        <a
          href="#apply"
          data-testid="elite-final-apply"
          className="inline-block mt-10 px-10 py-5 rounded-lg font-bebas text-2xl tracking-[0.3em] text-black transition-all hover:scale-[1.03]"
          style={{ backgroundColor: GREEN, boxShadow: `0 0 40px ${GREEN}60` }}
        >
          {es ? "APLICAR A ELITE" : "APPLY FOR ELITE"} →
        </a>
        <div className="mt-6 flex gap-3 justify-center flex-wrap">
          <span className="px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.25em] border" style={{ borderColor: `${GREEN}40`, color: GREEN }}>
            {es ? "ROSTER LIMITADO" : "LIMITED ROSTER"}
          </span>
          <span className="px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.25em] border" style={{ borderColor: `${GREEN}40`, color: GREEN }}>
            {es ? "SELECCIÓN REQUERIDA" : "SELECTION REQUIRED"}
          </span>
        </div>
      </div>
    </section>
  );
}

// no-op hook (kept for future context)
export function useEliteRef() {
  const r = useRef(null);
  useEffect(() => {}, []);
  return r;
}
